(function (root) {
  'use strict';

  const EXPECTED = Object.freeze({
    black: Object.freeze({
      post: Object.freeze({
        standard: ['58721072021852', 'OB-ECO-CAFE-PB'],
        'heavy-duty': ['58721072054620', 'OB-ECO-CAFE-PB-HD']
      }),
      clip: Object.freeze({ pair: ['58721072087388', 'OB-CAFE-CLIP'] }),
      rail: Object.freeze({
        '1000': ['58721072120156', 'OB-ECO-CAFE-100'],
        '1500': ['58721072152924', 'OB-ECO-CAFE-150'],
        '2000': ['58721072185692', 'OB-ECO-CAFE-200']
      })
    }),
    chrome: Object.freeze({
      post: Object.freeze({
        standard: ['58721084670300', 'OB-POP-CAFE-PB'],
        'heavy-duty': ['58721084703068', 'OB-POP-CAFE-PB-HD']
      }),
      clip: Object.freeze({ pair: ['58721084735836', 'OB-CAFE-CLIP-CH'] }),
      rail: Object.freeze({
        '1000': ['58721084768604', 'OB-POP-CAFE-100'],
        '1500': ['58721084801372', 'OB-POP-CAFE-150'],
        '2000': ['58721084834140', 'OB-POP-CAFE-200']
      })
    }),
    graphic: Object.freeze({
      '1000:single:spring_clip': ['59407356068188', 'OPS-CB-FAB-1000-SS-SC'],
      '1000:single:rail_pocket': ['59407356100956', 'OPS-CB-FAB-1000-SS-RL'],
      '1000:double:spring_clip': ['59407356133724', 'OPS-CB-FAB-1000-DS-SC'],
      '1000:double:rail_pocket': ['59407356166492', 'OPS-CB-FAB-1000-DS-RL'],
      '1500:single:spring_clip': ['59407356199260', 'OPS-CB-FAB-1500-SS-SC'],
      '1500:single:rail_pocket': ['59407356232028', 'OPS-CB-FAB-1500-SS-RL'],
      '1500:double:spring_clip': ['59407356264796', 'OPS-CB-FAB-1500-DS-SC'],
      '1500:double:rail_pocket': ['59407356297564', 'OPS-CB-FAB-1500-DS-RL'],
      '2000:single:spring_clip': ['59407356330332', 'OPS-CB-FAB-2000-SS-SC'],
      '2000:single:rail_pocket': ['59407356363100', 'OPS-CB-FAB-2000-SS-RL'],
      '2000:double:spring_clip': ['59407356395868', 'OPS-CB-FAB-2000-DS-SC'],
      '2000:double:rail_pocket': ['59407356428636', 'OPS-CB-FAB-2000-DS-RL']
    })
  });

  const POST_COUNTS = Object.freeze({ straight: 2, extension: 1, l_shaped: 3, c_shaped: 4, replacement_graphics: 0 });
  const PANEL_COUNTS = Object.freeze({ straight: 1, extension: 1, l_shaped: 2, c_shaped: 3 });
  const GRAPHIC_PRODUCT_ID = '15875067380060';

  function safePence(value) {
    const amount = Number(value);
    return Number.isSafeInteger(amount) && amount > 0 ? amount : 0;
  }

  function variantIndex(source) {
    const options = Array.isArray(source) ? source : Array.from(source?.options || []);
    const index = new Map();
    for (const option of options) {
      const data = option?.dataset || option;
      const sku = String(data.sku || '');
      if (!sku || index.has(sku)) continue;
      index.set(sku, {
        id: String(option.value ?? data.id ?? ''),
        sku,
        productId: String(data.productId || ''),
        productTitle: String(data.productTitle || ''),
        productHandle: String(data.productHandle || ''),
        title: String(data.title || ''),
        pricePence: safePence(data.pricePence),
        available: data.available === true || data.available === 'true'
      });
    }
    return index;
  }

  function lookup(index, expected) {
    if (!expected) return null;
    const [id, sku] = expected;
    const variant = index.get(sku);
    if (!variant || variant.id !== id || !variant.productId || !variant.available || !variant.pricePence) return null;
    if (!/^[1-9]\d*$/.test(id) || !/^[1-9]\d*$/.test(variant.productId)) return null;
    if (sku.startsWith('OPS-CB-FAB-') && variant.productId !== GRAPHIC_PRODUCT_ID) return null;
    return variant;
  }

  function createPlan(draft, variants) {
    if (!draft || !Object.hasOwn(POST_COUNTS, draft.layout) || !Object.hasOwn(EXPECTED, draft.finish)) return null;
    if (draft.layout === 'replacement_graphics') {
      if (!Array.isArray(draft.panels) || draft.panels.length < 1 || draft.panels.length > 3) return null;
    } else if (!Array.isArray(draft.panels) || draft.panels.length !== PANEL_COUNTS[draft.layout]) return null;
    if (!['standard', 'heavy-duty'].includes(draft.base)) return null;
    if (draft.layout === 'extension' && (draft.existingCompatiblePostConfirmed !== true ||
      !String(draft.existingSystemReference || '').trim())) return null;

    const index = variantIndex(variants);
    const lines = [];
    const add = (expected, role, quantity, panelId = '') => {
      const variant = lookup(index, expected);
      if (!variant || !Number.isSafeInteger(quantity) || quantity < 1) return false;
      lines.push(Object.freeze({
        id: Number(variant.id),
        sku: variant.sku,
        productId: variant.productId,
        productTitle: variant.productTitle,
        productHandle: variant.productHandle,
        variantTitle: variant.title,
        role,
        panelId,
        quantity,
        pricePence: variant.pricePence
      }));
      return true;
    };

    const count = POST_COUNTS[draft.layout];
    if (count && !add(EXPECTED[draft.finish].post[draft.base], 'post', count)) return null;
    for (const [indexInDraft, panel] of draft.panels.entries()) {
      if (![1000, 1500, 2000].includes(panel.widthMm) ||
          !['single', 'double'].includes(panel.sidedness) ||
          !['spring_clip', 'rail_pocket'].includes(panel.fixing)) return null;
      const panelId = String.fromCharCode(65 + indexInDraft);
      const graphicsKey = panel.widthMm + ':' + panel.sidedness + ':' + panel.fixing;
      if (count) {
        if (!add(EXPECTED[draft.finish].rail[String(panel.widthMm)], 'rail',
          panel.fixing === 'rail_pocket' ? 2 : 1, panelId)) return null;
        if (panel.fixing === 'spring_clip' && !add(EXPECTED[draft.finish].clip.pair, 'clip', 1, panelId)) return null;
      }
      if (!add(EXPECTED.graphic[graphicsKey], 'graphic', 1, panelId)) return null;
    }
    if (!lines.some((line) => line.role === 'graphic')) return null;
    const totalPence = lines.reduce((sum, line) => sum + line.quantity * line.pricePence, 0);
    if (!Number.isSafeInteger(totalPence) || totalPence < 1) return null;
    const planKey = [draft.layout, draft.finish, draft.base, ...draft.panels.map((panel) =>
      [panel.widthMm, panel.sidedness, panel.fixing].join(':'))].join('|');
    const primary = lines.find((line) => line.role === 'graphic');
    return Object.freeze({
      kind: 'modular',
      finish: draft.finish,
      layout: draft.layout,
      base: draft.base,
      planKey,
      totalPence,
      priceDisplay: new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(totalPence / 100),
      productTitle: primary.productTitle || 'Café barrier fabric graphics',
      productHandle: primary.productHandle || 'replacement-cafe-barrier-fabric-graphics-no-hardware',
      variantTitle: 'Modular café barrier configuration',
      sku: 'OPS-CB-MODULAR',
      lines: Object.freeze(lines)
    });
  }

  function buildCartItems(plan, snapshot, draft) {
    if (plan?.kind !== 'modular' || snapshot?.immutable !== true ||
        snapshot.sessionId !== snapshot.finalizedSessionId ||
        !snapshot.configurationId || !snapshot.uploadReference || !snapshot.zipUrl || !snapshot.previewUrl ||
        !Array.isArray(plan.lines) || !plan.lines.length) throw new Error('Unverified modular package.');
    const prefix = {
      '_ops_cafe_barrier_cart_v2': 'component-bom',
      '_ops_cafe_barrier_configuration_id': snapshot.configurationId,
      '_upload_session_id': snapshot.sessionId,
      '_upload_reference': snapshot.uploadReference,
      'Layout': ({ l_shaped:'L shape', c_shaped:'C shape', straight:'Straight',
        extension:'Extension', replacement_graphics:'Replacement graphics' })[draft.layout] || draft.layout,
      'Frame finish': draft.finish === 'chrome' ? 'Chrome' : 'Black'
    };
    return plan.lines.map((line, index) => {
      const panel = draft.panels.find((p) => p.panelId === line.panelId);
      const properties = {
        ...prefix,
        '_ops_cafe_barrier_line_uid': snapshot.configurationId + ':' + index,
        '_ops_cafe_barrier_component_role': line.role,
        '_ops_cafe_barrier_component_sku': line.sku
      };
      if (line.panelId) properties.Panel = line.panelId;
      if (panel) {
        properties['Panel width'] = panel.widthMm + ' mm';
        properties.Fixing = panel.fixing === 'spring_clip' ? 'Spring clips' : 'Rail pockets';
        properties['Printed sides'] = panel.sidedness === 'double' ? 'Double-sided' : 'Single-sided';
      }
      if (line.role === 'graphic') {
        properties['_Artwork files'] = snapshot.zipUrl;
        properties['_Artwork preview'] = snapshot.previewUrl;
        if (panel) properties['Artwork sides'] = panel.sidedness === 'double' ?
          (panel.sameArtworkBothSides ? 'Same artwork both sides' : 'Separate front and back artwork') :
          'Single-sided artwork';
      }
      if (line.role === 'post') {
        properties['Post and base'] = draft.base === 'heavy-duty' ? 'Heavy duty' : 'Standard';
      }
      return { id: line.id, quantity: line.quantity, properties };
    });
  }

  function matchesCart(expected, actual, sessionId, configurationId) {
    if (!Array.isArray(actual)) return false;
    return expected.every((item) => actual.some((line) =>
      String(line.variant_id || line.id || '') === String(item.id) &&
      line.properties?._ops_cafe_barrier_line_uid === item.properties._ops_cafe_barrier_line_uid &&
      line.properties?._upload_session_id === sessionId &&
      line.properties?._ops_cafe_barrier_configuration_id === configurationId &&
      Number(line.quantity) === item.quantity
    ));
  }

  const api = Object.freeze({ EXPECTED, GRAPHIC_PRODUCT_ID, variantIndex, createPlan, buildCartItems, matchesCart });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.OpsCafeBarrierComponentCart = api;
})(typeof window !== 'undefined' ? window : globalThis);
